// src/components/Admin/AdminDashboard.jsx
import React, { useState } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { ArrowLeft, Menu, Settings } from "lucide-react";
import { selectUser, selectClientAccess } from "../../store/authSlice";
import AdminSidebar from "./AdminSidebar";
import { ToastProvider } from "./shared/ToastContext";
import AdminErrorBoundary from "./shared/ErrorBoundary";

/**
 * Admin Dashboard layout shell.
 * Renders the sidebar, header, and an <Outlet> for nested admin routes.
 * Wraps content in ToastProvider + ErrorBoundary.
 */
const AdminDashboard = () => {
  const user = useSelector(selectUser);
  const clientAccess = useSelector(selectClientAccess);
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Determine the "Back to Map" link — use first accessible client or fallback
  const backToMapSlug = clientAccess?.[0] || "laundry-lifesaver";

  // Derive page title from current route
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === "/admin") return "Clients";
    if (path.startsWith("/admin/clients/new")) return "New Client";
    if (path.startsWith("/admin/clients/")) return "Edit Client";
    if (path.startsWith("/admin/geography")) return "Geography Library";
    if (path.startsWith("/admin/faq")) return "FAQ Manager";
    return "Admin";
  };

  return (
    <ToastProvider>
      <div className="min-h-screen bg-gray-50 flex flex-col">
        {/* Top header bar */}
        <header className="bg-primary text-white shadow-md flex-shrink-0 z-30">
          <div className="px-4 py-3 sm:py-4">
            <div className="flex items-center justify-between">
              {/* Left: Mobile menu + Back to Map */}
              <div className="flex items-center gap-3">
                {/* Mobile hamburger */}
                <button
                  onClick={() => setMobileMenuOpen(true)}
                  className="lg:hidden text-gray-300 hover:text-white transition"
                >
                  <Menu size={22} />
                </button>

                <Link
                  to={`/${backToMapSlug}`}
                  className="flex items-center gap-1.5 text-gray-300 hover:text-white transition flex-shrink-0"
                >
                  <ArrowLeft size={18} />
                  <span className="text-sm whitespace-nowrap hidden sm:inline">
                    Back to Map
                  </span>
                </Link>
              </div>

              {/* Center: page title (mobile-friendly) */}
              <h1 className="text-base sm:text-lg font-semibold lg:hidden">
                {getPageTitle()}
              </h1>

              {/* Right: user info */}
              <div className="flex items-center gap-1.5 sm:gap-3">
                <Settings
                  size={16}
                  className="text-gray-300 flex-shrink-0 sm:w-5 sm:h-5"
                />
                <span className="text-xs sm:text-sm text-gray-300 truncate max-w-[120px] sm:max-w-none">
                  {user?.email}
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Body: sidebar + content */}
        <div className="flex flex-1 overflow-hidden">
          <AdminSidebar
            mobileOpen={mobileMenuOpen}
            onMobileClose={() => setMobileMenuOpen(false)}
          />

          {/* Main content area */}
          <main className="flex-1 overflow-y-auto">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
              <AdminErrorBoundary>
                <Outlet />
              </AdminErrorBoundary>
            </div>
          </main>
        </div>
      </div>
    </ToastProvider>
  );
};

export default AdminDashboard;
